import type {
    Brand,
    BrandId,
    BuyStep
} from '@/types'

import {
    BookOpen,
    Briefcase,
    BriefcaseBusiness,
    GraduationCap,
    Languages
} from 'lucide-react'

/* The program the site opens with. Visitors can switch, and their choice is remembered. */
export const DEFAULT_BRAND: BrandId = 'jlpt'

export const BRAND_ORDER: BrandId[] = ['jlpt', 'jft']

export const BRAND_STORAGE_KEY = 'hajime-program'

/* Shown under the catalog in both programs. */
export const BUY_STEPS: BuyStep[] = [
    {
        title: 'Pilih file',
        text: 'Masukkan ebook ke keranjang, satu atau beberapa sekaligus.'
    },
    {
        title: 'Bayar',
        text: 'Masuk ke akunmu, lalu pilih bayar online atau transfer bank.'
    },
    {
        title: 'Unduh',
        text: 'Setelah pembayaran diterima, unduh file kapan saja dari Perpustakaan.'
    }
]

export const BRANDS: Record<BrandId, Brand> = {
    jlpt: {
        id: 'jlpt',
        label: 'JLPT',
        wordmark: 'hajime',
        company: 'Hajime Nihongo',
        headline: 'JLPT kamu',
        tagline: 'Ebook bahasa Jepang untuk pemula, ditulis dalam bahasa Indonesia.',
        icon: GraduationCap,
        markIcon: BookOpen,
        examIcon: GraduationCap,
        products: [],
        story: {
            title: 'Belajar bahasa Jepang dari nol, dengan bahasa yang kamu pahami.',
            paragraphs: [
                "Banyak buku JLPT ditulis dalam bahasa Inggris atau langsung bahasa Jepang, jadi pemula sering bingung sebelum benar-benar mulai. Karena itu semua ebook Hajime ditulis dalam bahasa Indonesia, dengan contoh yang dekat dengan keseharian kita.",
                "Mulai dari hiragana, lanjut ke kosakata, pola kalimat dan kanji, lalu uji dirimu dengan simulasi ujian. Semua file bisa dibuka di HP, tablet atau laptop, dan bisa dicetak kalau kamu lebih suka belajar di atas kertas."
            ],
            image: '/images/story/jlpt.jpg',
            imageAlt: 'Seorang siswi membaca dengan serius di mejanya, dikelilingi buku pelajaran',
            badgeValue: 'N5 → N4',
            badgeLabel: 'Jalur belajar untuk pemula',
            stats: [
                { value: '6', label: 'Ebook dan file latihan untuk N5 dan N4' },
                { value: '100%', label: 'Penjelasan dalam bahasa Indonesia' },
                { value: '3', label: 'Format: PDF, audio MP3 dan dek Anki' }
            ]
        },
        catalog: {
            title: 'Enam ebook untuk lulus N5 dan N4.',
            description: "Setiap paket berisi ebook PDF yang rapi, ditambah audio, dek Anki atau lembar latihan sesuai materinya. Beli satu, atau kumpulkan semuanya."
        },
        roadmap: {
            title: 'Dari nol sampai siap ujian N5 dalam empat tahap.',
            steps: [
                {
                    title: 'Huruf',
                    text: "Kuasai hiragana dan katakana lewat latihan menulis dan audio pelafalan, cukup 15 menit sehari.",
                    image: '/images/roadmap/jlpt-letters.jpg',
                    alt: 'Kuas menggoreskan tinta hitam di atas kertas putih'
                },
                {
                    title: 'Kosakata',
                    text: "Hafalkan sekitar 800 kosakata N5 dengan dek Anki dan contoh kalimat yang mudah diingat.",
                    image: '/images/roadmap/jlpt-vocabulary.jpg',
                    alt: 'Sekelompok pelajar belajar bersama dan mencatat di ruang kelas yang terang'
                },
                {
                    title: 'Pola kalimat',
                    text: "Pahami tata bahasa N5 lewat penjelasan bahasa Indonesia, tabel ringkas dan latihan di setiap bab.",
                    image: '/images/roadmap/jlpt-grammar.jpg',
                    alt: 'Buku catatan terbuka berisi tulisan tangan, pulpen perak dan laptop di atas meja'
                },
                {
                    title: 'Simulasi',
                    text: "Uji kesiapanmu dengan tryout bergaya JLPT, lengkap dengan audio choukai dan pembahasan.",
                    image: '/images/roadmap/jlpt-exam.jpg',
                    alt: 'Sebatang pensil putih dengan penghapus merah muda'
                }
            ]
        },
        exams: {
            title: 'Dua level pertama, dua langkah menuju Jepang.',
            description: "JLPT diadakan dua kali setahun di Indonesia, biasanya bulan Juli dan Desember. Untuk pemula, targetnya N5 lebih dulu, lalu N4.",
            items: [
                {
                    eyebrow: 'Level paling dasar',
                    name: 'N5',
                    fullName: 'Japanese-Language Proficiency Test',
                    description: "Mengukur kemampuan memahami bahasa Jepang dasar: hiragana, katakana, kanji sederhana dan percakapan pendek sehari-hari yang diucapkan perlahan.",
                    contourAt: '85% 115%',
                    facts: [
                        { label: 'Kanji', value: '± 100' },
                        { label: 'Kosakata', value: '± 800' },
                        { label: 'Durasi', value: '90 menit' }
                    ]
                },
                {
                    eyebrow: 'Langkah berikutnya',
                    name: 'N4',
                    fullName: 'Japanese-Language Proficiency Test',
                    description: "Mengukur pemahaman bahasa Jepang dasar yang lebih luas. N4 juga diterima sebagai syarat bahasa untuk visa kerja Tokutei Ginou.",
                    contourAt: '15% -15%',
                    facts: [
                        { label: 'Kanji', value: '± 300' },
                        { label: 'Kosakata', value: '± 1.500' },
                        { label: 'Durasi', value: '115 menit' }
                    ]
                }
            ]
        }
    },
    jft: {
        id: 'jft',
        label: 'JFT',
        wordmark: 'hajime',
        company: 'Hajime Nihongo',
        headline: 'JFT kamu',
        tagline: 'Persiapan JFT-Basic untuk kamu yang ingin bekerja di Jepang.',
        icon: BriefcaseBusiness,
        markIcon: Languages,
        examIcon: Briefcase,
        products: [],
        story: {
            title: 'Siapkan JFT-Basic, lalu wujudkan kerja di Jepang.',
            paragraphs: [
                "JFT-Basic adalah ujian bahasa Jepang berbasis komputer yang dipakai sebagai syarat bahasa untuk visa kerja Tokutei Ginou. Soalnya praktis: membaca tanda, memahami pengumuman dan bercakap di tempat kerja.",
                "Materi JFT kami fokus pada situasi nyata yang akan kamu temui di tempat kerja, di toko dan di fasilitas umum, lengkap dengan audio dan simulasi ujian seperti di layar komputer."
            ],
            image: '/images/story/jft.jpg',
            imageAlt: 'Seorang perempuan muda memakai headphone putih sambil menunduk fokus',
            badgeValue: 'A2',
            badgeLabel: 'Level yang dibutuhkan untuk lulus JFT-Basic',
            stats: [
                { value: '6', label: 'Ebook dan file latihan khusus JFT-Basic' },
                { value: '4', label: 'Bagian ujian yang kami bahas tuntas' },
                { value: '200', label: 'Skor minimal lulus, dari maksimal 250' }
            ]
        },
        catalog: {
            title: 'Enam ebook untuk lulus JFT-Basic.',
            description: "Materi disusun mengikuti empat bagian ujian JFT-Basic, dari kosakata kerja sampai simulasi CBT, lengkap dengan audio dan kunci jawaban."
        },
        roadmap: {
            title: 'Dari nol sampai siap kerja di Jepang dalam empat tahap.',
            steps: [
                {
                    title: 'Kenali ujian',
                    text: "Pahami format CBT, empat bagian soal dan cara mendaftar ujian JFT-Basic di Indonesia.",
                    image: '/images/roadmap/jft-exam.jpg',
                    alt: 'Papan petunjuk jalan bertuliskan new things di depan dinding merah muda dan langit biru'
                },
                {
                    title: 'Kosakata kerja',
                    text: "Pelajari kata yang paling sering muncul di tempat kerja dan kehidupan sehari-hari di Jepang.",
                    image: '/images/roadmap/jft-vocabulary.jpg',
                    alt: 'Seorang pekerja berjas putih berdiri di lorong pabrik modern'
                },
                {
                    title: 'Percakapan',
                    text: "Latih ungkapan untuk menyapa, bertanya, melapor dan meminta tolong, dengan audio untuk ditirukan.",
                    image: '/images/roadmap/jft-conversation.jpg',
                    alt: 'Rekan kerja mengobrol santai di dapur kantor yang terang'
                },
                {
                    title: 'Simulasi',
                    text: "Kerjakan simulasi bergaya CBT dengan batas waktu, lalu cek perkiraan skor dan pembahasannya.",
                    image: '/images/roadmap/jft-simulation.jpg',
                    alt: 'Seorang pria memakai headset tersenyum sambil bekerja di depan komputer'
                }
            ]
        },
        exams: {
            title: 'Satu ujian bahasa, satu pintu ke visa kerja.',
            description: "JFT-Basic diselenggarakan The Japan Foundation dan bisa diikuti berkali-kali dalam setahun di pusat ujian di berbagai kota di Indonesia.",
            items: [
                {
                    eyebrow: 'Ujian bahasa',
                    name: 'JFT-Basic',
                    fullName: 'Japan Foundation Test for Basic Japanese',
                    description: "Ujian berbasis komputer dengan empat bagian: huruf dan kosakata, percakapan dan ungkapan, mendengar, serta membaca. Skor 200 ke atas berarti kamu mencapai level A2.",
                    contourAt: '85% 115%',
                    facts: [
                        { label: 'Format', value: 'CBT' },
                        { label: 'Durasi', value: '± 60 menit' },
                        { label: 'Lulus', value: '200 / 250' }
                    ]
                },
                {
                    eyebrow: 'Tujuan akhir',
                    name: 'Tokutei Ginou',
                    fullName: 'Visa Specified Skilled Worker (SSW)',
                    description: "Visa kerja untuk tenaga terampil di bidang yang kekurangan pekerja. Syaratnya lulus ujian keterampilan bidang dan ujian bahasa, yaitu JFT-Basic A2 atau JLPT N4.",
                    contourAt: '15% -15%',
                    facts: [
                        { label: 'Bahasa', value: 'A2 / N4' },
                        { label: 'Ujian', value: 'Bahasa + bidang' },
                        { label: 'Masa kerja', value: 'Hingga 5 tahun' }
                    ]
                }
            ]
        }
    }
}
